import React, { useState } from 'react'
import { Input, Table } from 'antd'
import type { TableColumnsType } from 'antd'
import axios from 'axios'
import { appApiUrls } from '@shared/url-map.js'
import { POSTHOG_PROJECT_URL, RC_PROXY_APP_USER_LOG_LIMIT } from '@shared/constants.js'
import type { RcProxyLogRow } from '@shared/common-types.js'
import Card from 'src/client/components/Card.jsx'
import SimpleTable from 'src/client/components/SimpleTable.jsx'
import MultiLineChartCard from 'src/client/components/charts/MultiLineChartCard.jsx'
import { useChartData } from 'src/client/hooks/useChartData.js'

/** 和图表里的时间一样按北京时间显示，不跟浏览器所在时区走 */
function formatShanghaiTime(iso: string) {
  return new Date(iso).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })
}

const searchColumns: TableColumnsType<RcProxyLogRow> = [
  { title: '时间', dataIndex: 'createdAt', render: formatShanghaiTime, width: 170 },
  { title: 'method', dataIndex: 'method', width: 80 },
  { title: 'path', dataIndex: 'path', ellipsis: true },
  // 0 是客户端在响应发出前就断开了
  { title: 'status', dataIndex: 'status', width: 80, render: (status: number) => status || '-' },
  { title: '耗时 ms', dataIndex: 'durationMs', width: 90 },
  { title: 'ip', dataIndex: 'ip', width: 140, ellipsis: true },
  { title: 'error', dataIndex: 'error', width: 180, render: (error: string | null) => error ?? '' },
]

function RecentIssuesCard() {
  const { data, loading, error } = useChartData('rc-proxy-recent-issues')
  const title = '最近100条出错 / 慢 / 客户端先断开的请求'
  if (loading) return <Card title={title}>loading...</Card>
  if (error || !data) {
    return (
      <Card title={title}>
        <span className="text-red-400">加载失败</span>
      </Card>
    )
  }
  return (
    <Card title={title}>
      <SimpleTable option={{ dataset: { source: data.rows } }} />
    </Card>
  )
}

function AppUserSearchCard() {
  const [rows, setRows] = useState<RcProxyLogRow[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  const search = async (value: string) => {
    const appUserId = value.trim()
    if (!appUserId) return
    setLoading(true)
    setFailed(false)
    try {
      const res = await axios.get(appApiUrls.revenueCatProxyLogs, { params: { appUserId } })
      if (res.data.code === 0) {
        setRows(res.data.data.list as RcProxyLogRow[])
      } else {
        setFailed(true)
      }
    } catch (error) {
      console.error('Failed to search rc proxy logs', error)
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card title={`按 appUserID 查（最近 ${RC_PROXY_APP_USER_LOG_LIMIT} 条）`}>
      <div className="flex flex-col gap-2 p-2">
        <Input.Search
          placeholder="appUserID，例如 $RCAnonymousID:... 或账号 id"
          allowClear
          enterButton="查询"
          loading={loading}
          onSearch={search}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          收据请求（POST /v1/receipts）的 appUserID 取自请求体里的 app_user_id 字段，请求体本身不存。
        </p>
        {failed && <span className="text-red-400">查询失败</span>}
        {rows && (
          <Table
            columns={searchColumns}
            dataSource={rows}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ x: true, y: 400 }}
          />
        )}
      </div>
    </Card>
  )
}

export default function RevenueCatProxyDashboard() {
  return (
    <div className="m-2 flex flex-col gap-2">
      <h1 className="text-lg font-bold">RevenueCat 代理</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        这里只有到了我们服务器的请求，而且只记收据等非 GET、&gt;=400、出错、&gt;5 秒、客户端先断开的请求，量大的成功 GET
        不记。在到达服务器之前就失败的请求（比如用户的 Wi-Fi 连不上 cn.deergo.app）这里永远看不到，要看客户端埋点：
        <a className="text-blue-500" href={POSTHOG_PROJECT_URL} target="_blank" rel="noreferrer">
          在 PostHog 查看 ↗
        </a>
        （api_request_failed 的 error_code -1004，purchase_failure，以及 purchase_completed 里的
        rc_sync_pending）。今天的数在位置分析页顶上的卡片里。
      </p>
      <MultiLineChartCard chartId="rc-proxy-30-day-trend" title="30天趋势（北京时间）" />
      <p className="px-1 text-xs text-gray-500 dark:text-gray-400">
        errors：status &gt;= 400 或有 error（4xx 含收据被 RC 拒、key 不对；5xx 含上游不可达时回的 502；error
        是上游超时、连接被重置等）。匿名 ID 的属性上报 404 不算：那是登录前 SDK 用还没建档的匿名 ID 报属性，SDK
        会自己重试； 正式账号的属性 404 仍然算，那说明 RC 上找不到这个已登录的用户。 不是 SDK
        路径的请求（扫描器）在代理入口直接拦掉，不进这张表；slow：耗时 &gt; 5
        秒；client_left：客户端在响应发出前就断开（表里 status 记 0）。
      </p>
      <RecentIssuesCard />
      <AppUserSearchCard />
    </div>
  )
}
